import { formatCurrency, formatDateTime } from "./formato";

// ------------------------------------------------------------
// Comprobantes para mandar por WhatsApp.
//
// La imagen se dibuja en un <canvas> con las herramientas del propio
// navegador: sin librerías, sin pedirle nada al servidor y pesando unos
// pocos KB.
//
// Cómo se comparte depende del aparato:
//   - En el celular se abre la hoja de compartir del sistema con la
//     imagen adjunta, y WhatsApp aparece ahí.
//   - En la computadora no se puede adjuntar un archivo a WhatsApp desde
//     un enlace (lo impide WhatsApp), así que la imagen se copia al
//     portapapeles para pegarla en el chat con Ctrl+V.
//   - Si nada de eso está disponible, se descarga.
// ------------------------------------------------------------

const ANCHO = 680;
const MARGEN = 48;
const ESCALA = 2; // Para que se vea nítida en pantallas de teléfono.

const AZUL = "#0d6efd";
const TINTA = "#16191d";
const GRIS = "#6c757d";
const GRIS_CLARO = "#adb5bd";
const LINEA = "#e9ecef";
const AMBAR = "#b45309";
const AMBAR_FONDO = "#fdf8f1";
const VERDE = "#0f7b4f";
const VERDE_FONDO = "#e7f4ee";

const FUENTE = '"Inter", -apple-system, "Segoe UI", Roboto, sans-serif';

const fecha = (valor) =>
  new Intl.DateTimeFormat("es-CR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(valor ? new Date(valor) : new Date());

function rectanguloRedondeado(ctx, x, y, ancho, alto, radio) {
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(x, y, ancho, alto, radio);
    return;
  }
  ctx.beginPath();
  ctx.moveTo(x + radio, y);
  ctx.arcTo(x + ancho, y, x + ancho, y + alto, radio);
  ctx.arcTo(x + ancho, y + alto, x, y + alto, radio);
  ctx.arcTo(x, y + alto, x, y, radio);
  ctx.arcTo(x, y, x + ancho, y, radio);
  ctx.closePath();
}

/**
 * Dibuja el comprobante y devuelve la imagen como Blob.
 *
 * @param {string} titulo      "Estado de cuenta" o "Comprobante de abono"
 * @param {string} cliente     Nombre del cliente
 * @param {string} subtitulo   Fecha, o fecha y hora del movimiento
 * @param {Array}  filas       [{ rotulo, valor, color? }]
 * @param {Object} total       { rotulo, monto, positivo }
 */
async function dibujar({ titulo, cliente, subtitulo, filas, total }) {
  // Sin esperar a las fuentes, el navegador dibuja con la de reemplazo.
  if (document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch (e) {
      /* si falla, se dibuja igual */
    }
  }

  const altoFilas = filas.length * 42;
  const alto = 300 + altoFilas + 128;

  const canvas = document.createElement("canvas");
  canvas.width = ANCHO * ESCALA;
  canvas.height = alto * ESCALA;
  const ctx = canvas.getContext("2d");
  ctx.scale(ESCALA, ESCALA);
  ctx.textBaseline = "alphabetic";

  // Fondo
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, ANCHO, alto);

  // Franja de color arriba
  ctx.fillStyle = AZUL;
  ctx.fillRect(0, 0, ANCHO, 8);

  let y = 78;

  // Marca y título
  ctx.textAlign = "center";
  ctx.fillStyle = TINTA;
  ctx.font = `700 27px ${FUENTE}`;
  ctx.fillText("RÚSTIKO", ANCHO / 2, y);

  y += 28;
  ctx.fillStyle = GRIS;
  ctx.font = `500 15px ${FUENTE}`;
  ctx.fillText(titulo, ANCHO / 2, y);

  // Datos del cliente
  y += 52;
  ctx.fillStyle = TINTA;
  ctx.font = `700 25px ${FUENTE}`;
  ctx.fillText(cliente, ANCHO / 2, y);

  if (subtitulo) {
    y += 26;
    ctx.fillStyle = GRIS_CLARO;
    ctx.font = `400 14px ${FUENTE}`;
    ctx.fillText(subtitulo, ANCHO / 2, y);
  }

  // Separador entre los datos del cliente y las cifras
  y += 34;
  ctx.strokeStyle = LINEA;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(MARGEN, y);
  ctx.lineTo(ANCHO - MARGEN, y);
  ctx.stroke();

  // Filas. Puede no haber ninguna: el estado de cuenta lleva solo el
  // saldo, sin desglose.
  y += filas.length > 0 ? 38 : 20;
  filas.forEach((fila) => {
    ctx.textAlign = "left";
    ctx.fillStyle = GRIS;
    ctx.font = `400 16px ${FUENTE}`;
    ctx.fillText(fila.rotulo, MARGEN, y);

    ctx.textAlign = "right";
    ctx.fillStyle = fila.color || TINTA;
    ctx.font = `600 17px ${FUENTE}`;
    ctx.fillText(fila.valor, ANCHO - MARGEN, y);

    y += 42;
  });

  // Total, en su caja de color
  y += 8;
  const altoCaja = 88;
  ctx.fillStyle = total.positivo ? AMBAR_FONDO : VERDE_FONDO;
  rectanguloRedondeado(ctx, MARGEN, y, ANCHO - MARGEN * 2, altoCaja, 14);
  ctx.fill();

  ctx.textAlign = "left";
  ctx.fillStyle = total.positivo ? AMBAR : VERDE;
  ctx.font = `600 13px ${FUENTE}`;
  ctx.fillText(total.rotulo.toUpperCase(), MARGEN + 22, y + 34);

  ctx.textAlign = "right";
  ctx.font = `700 32px ${FUENTE}`;
  ctx.fillText(total.monto, ANCHO - MARGEN - 22, y + 62);

  // Pie
  y += altoCaja + 40;
  ctx.textAlign = "center";
  ctx.fillStyle = GRIS_CLARO;
  ctx.font = `400 12px ${FUENTE}`;
  ctx.fillText(`Generado el ${fecha()}`, ANCHO / 2, y);

  return new Promise((resolver) => canvas.toBlob(resolver, "image/png"));
}

/**
 * Estado de cuenta: los datos del cliente y lo que debe hoy.
 * Sin desglose a propósito: lo que se manda por WhatsApp es la
 * respuesta a "¿cuánto debo?", no un informe.
 */
export function estadoDeCuenta({ cliente, saldos }) {
  const saldo = Number(saldos.saldo_pendiente) || 0;

  return dibujar({
    titulo: "Estado de cuenta",
    cliente: cliente.nombre_completo,
    subtitulo: cliente.telefono && cliente.telefono !== "-" ? cliente.telefono : "",
    filas: [],
    total: {
      rotulo: saldo > 0 ? "Saldo pendiente" : "Saldo",
      monto: saldo > 0 ? formatCurrency(saldo) : "Al día",
      positivo: saldo > 0,
    },
  });
}

/** Comprobante de un abono: cuánto pagó y cuánto queda debiendo. */
export function comprobanteDeAbono({ cliente, detalle, formaPago }) {
  const nuevo = Number(detalle.saldo_nuevo) || 0;

  const filas = [
    { rotulo: "Saldo anterior", valor: formatCurrency(detalle.saldo_anterior) },
    {
      rotulo: "Abono recibido",
      valor: `− ${formatCurrency(detalle.monto_abono)}`,
      color: VERDE,
    },
  ];
  if (formaPago) {
    filas.push({ rotulo: "Forma de pago", valor: formaPago });
  }

  return dibujar({
    titulo: "Comprobante de abono",
    cliente,
    subtitulo: formatDateTime(detalle.fecha),
    filas,
    total: {
      rotulo: nuevo > 0 ? "Saldo nuevo" : "Saldo",
      monto: nuevo > 0 ? formatCurrency(nuevo) : "Al día",
      positivo: nuevo > 0,
    },
  });
}

function descargar(blob, nombreArchivo) {
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = nombreArchivo;
  document.body.appendChild(enlace);
  enlace.click();
  document.body.removeChild(enlace);
  URL.revokeObjectURL(url);
}

/**
 * Comparte la imagen y devuelve qué fue lo que pasó:
 * "compartido" | "copiado" | "descargado" | "cancelado"
 */
export async function compartirImagen(blob, nombreArchivo, titulo) {
  const archivo = new File([blob], nombreArchivo, { type: "image/png" });

  // Celular: hoja de compartir del sistema, con WhatsApp adentro.
  if (navigator.canShare && navigator.canShare({ files: [archivo] })) {
    try {
      await navigator.share({ files: [archivo], title: titulo });
      return "compartido";
    } catch (error) {
      // Si la persona cierra la hoja de compartir no es un error.
      if (error && error.name === "AbortError") return "cancelado";
    }
  }

  // Computadora: al portapapeles, para pegarla en WhatsApp Web.
  if (navigator.clipboard && window.ClipboardItem) {
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      return "copiado";
    } catch (error) {
      /* algunos navegadores no lo permiten: se descarga */
    }
  }

  descargar(blob, nombreArchivo);
  return "descargado";
}
